import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign, TrendingUp, Package, ShoppingCart, MessageSquare } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";

const statusColor = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  fulfilled: "bg-slate-100 text-slate-800 border-slate-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

export default function Admin() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/admin/contacts").then((r) => setContacts(r.data));
  };

  useEffect(() => {
    if (ready && user?.role === "admin") load();
  }, [ready, user]);

  if (!ready) return null;
  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-2xl text-slate-900">Admin access only.</p>
        <p className="text-slate-500 mt-2">Sign in with an admin account to continue.</p>
      </div>
    );
  }

  const fulfill = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/fulfill`);
      toast.success("Marked as fulfilled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid={ADMIN.root} className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <p className="overline text-emerald-800 mb-2">Admin dashboard</p>
      <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
        Operations
      </h1>
      <p className="text-slate-500 mt-2 text-sm">
        Track revenue, distributor cost, margin, and forward paid orders to fulfillment.
      </p>

      {stats && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            testid={ADMIN.statRevenue}
            icon={DollarSign} label="Gross revenue"
            value={formatMoney(stats.revenue)} sub={`${stats.paid_orders} paid orders`}
          />
          <StatCard
            testid={ADMIN.statMargin}
            icon={TrendingUp} label="Net margin"
            value={formatMoney(stats.margin)}
            sub={`${stats.margin_pct}% · cost ${formatMoney(stats.cost)}`}
            accent
          />
          <StatCard
            testid={ADMIN.statOrders}
            icon={ShoppingCart} label="Total orders"
            value={stats.total_orders} sub={`${stats.pending_orders} pending`}
          />
          <StatCard
            testid={ADMIN.statPending}
            icon={Package} label="Active SKUs"
            value={stats.total_products}
            sub="Catalog"
          />
        </div>
      )}

      <div className="mt-12 border-b border-slate-200">
        <div className="flex gap-6">
          <TabBtn testid={ADMIN.tabOrders} active={tab === "orders"} onClick={() => setTab("orders")}>
            Orders ({orders.length})
          </TabBtn>
          <TabBtn testid={ADMIN.tabProducts} active={tab === "products"} onClick={() => setTab("products")}>
            Products ({products.length})
          </TabBtn>
          <TabBtn testid={ADMIN.tabContacts} active={tab === "contacts"} onClick={() => setTab("contacts")}>
            Contact requests ({contacts.length})
          </TabBtn>
        </div>
      </div>

      {tab === "orders" && (
        <div data-testid={ADMIN.ordersTable} className="mt-8 border border-slate-200 rounded-sm overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <Th>Order</Th>
                  <Th>Date</Th>
                  <Th>Customer</Th>
                  <Th>Items</Th>
                  <Th className="text-right">Retail</Th>
                  <Th className="text-right">Cost</Th>
                  <Th className="text-right">Margin</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.length === 0 && (
                  <tr><td colSpan={9} className="p-10 text-center text-slate-500">No orders yet.</td></tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50/50">
                    <Td>
                      <p className="font-mono text-xs">{o.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-[10px] text-slate-500">{o.session_id?.slice(-8)}</p>
                    </Td>
                    <Td>{new Date(o.created_at).toLocaleDateString()}</Td>
                    <Td>
                      <p className="text-slate-900">{o.shipping?.full_name}</p>
                      <p className="text-xs text-slate-500">{o.email}</p>
                    </Td>
                    <Td>{o.items.reduce((s, i) => s + i.quantity, 0)} units</Td>
                    <Td className="text-right font-medium">{formatMoney(o.total)}</Td>
                    <Td className="text-right text-slate-500">{formatMoney(o.cost_total)}</Td>
                    <Td className="text-right font-semibold text-emerald-800">{formatMoney(o.margin)}</Td>
                    <Td>
                      <span className={`px-2 py-0.5 text-xs rounded-sm border ${statusColor[o.status] || statusColor.pending}`}>
                        {o.status}
                      </span>
                    </Td>
                    <Td>
                      {o.status === "paid" && (
                        <button
                          data-testid={ADMIN.fulfillBtn(o.id)}
                          onClick={() => fulfill(o.id)}
                          className="text-xs px-3 py-1.5 bg-slate-900 text-white rounded-sm hover:bg-emerald-900"
                        >
                          Mark fulfilled
                        </button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "products" && (
        <div data-testid={ADMIN.productsTable} className="mt-8 border border-slate-200 rounded-sm overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <Th>Product</Th>
                  <Th>Category</Th>
                  <Th>SKU</Th>
                  <Th className="text-right">Cost</Th>
                  <Th className="text-right">Retail</Th>
                  <Th className="text-right">Margin</Th>
                  <Th className="text-right">Stock</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((p) => {
                  const m = p.retail_price - p.cost_price;
                  const pct = (m / p.retail_price) * 100;
                  return (
                    <tr key={p.id} className="hover:bg-slate-50/50">
                      <Td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden">
                            <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                          </div>
                          <span className="text-slate-900 line-clamp-1 max-w-xs">{p.name}</span>
                        </div>
                      </Td>
                      <Td className="capitalize">{p.category.replace("-", " ")}</Td>
                      <Td className="font-mono text-xs">{p.sku}</Td>
                      <Td className="text-right text-slate-500">{formatMoney(p.cost_price)}</Td>
                      <Td className="text-right font-medium">{formatMoney(p.retail_price)}</Td>
                      <Td className="text-right font-semibold text-emerald-800">
                        {formatMoney(m)} <span className="text-xs text-slate-500">({pct.toFixed(0)}%)</span>
                      </Td>
                      <Td className="text-right">{p.stock}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "contacts" && (
        <div className="mt-8 space-y-4">
          {contacts.length === 0 && (
            <div className="border border-dashed border-slate-300 p-16 text-center">
              <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No contact requests yet.</p>
            </div>
          )}
          {contacts.map((c) => (
            <div key={c.id} className="border border-slate-200 bg-white p-5 rounded-sm">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-medium text-slate-900">{c.name} {c.company && <span className="text-slate-500 font-normal">· {c.company}</span>}</p>
                  <p className="text-xs text-slate-500">{c.email}{c.phone && ` · ${c.phone}`}</p>
                </div>
                <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-line mt-2">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className={`border rounded-sm p-5 ${accent ? "bg-emerald-900 text-white border-emerald-900" : "bg-white border-slate-200"}`}>
      <div className={`flex items-center gap-2 mb-3 ${accent ? "text-emerald-200" : "text-slate-500"}`}>
        <Icon className="w-4 h-4" />
        <span className="overline">{label}</span>
      </div>
      <p className={`font-display text-3xl font-black ${accent ? "text-white" : "text-slate-900"}`}>{value}</p>
      <p className={`text-xs mt-1 ${accent ? "text-emerald-200" : "text-slate-500"}`}>{sub}</p>
    </div>
  );
}

function TabBtn({ active, children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`pb-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
        active
          ? "border-emerald-900 text-slate-900"
          : "border-transparent text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left font-medium uppercase tracking-wider text-[10px] ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}
